import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import reactStringReplace from 'react-string-replace';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { CATEGORY_HIERARCHY, CATEGORY_IMAGES } from '../../utils/constants/category';
import { useStyles } from './styles';
import CategoryCard from '../CategoryCard';
import Filters from './Filters';
import Search from './Search';
import AlgorithmPopover from './AlgorithmPopover';
import Link from '../../components/Link';
import { getDomain } from '../../utils/config';
import SupportCards from './SupportCards';
import { useDispatch, useSelector } from 'react-redux';
import useUpdateFilterValue from '../../hooks/useUpdateFilterValue';
import { RenderChatbotLink } from './utils';
import { updateHistory, updateMessages } from '../../redux/slices/chatBotSlice';

import api from '../../api';
import { Avatar, Box, Paper } from '@mui/material';
import LoadingDots from '../ChatComponent/LoadingDots';
import CHAT_ICON from '../../images/icons/AIChatIconOrange.svg';
import { getCurrentLanguage } from '../../utils/i18n';

function buildPath(messageUrl) {
	if (!messageUrl) return null;

	const separator = 'product-recommendation-tools-plus/';
	const extractedUrlSegment = messageUrl.split(separator);

	if (extractedUrlSegment.length > 1) {
		const result = extractedUrlSegment[1];
		const domain = window.location.origin;
		return `${domain}/design/tools-software/product-recommendation-tools-plus/${result}`;
	}
}

const CategoriesDashboard = () => {
	const classes = useStyles();
	const { t } = useTranslation(['main', 'category']);
	const domain = getDomain();
	const language = getCurrentLanguage();
	const [showChat, setShowChat] = useState(false);
	const allMessages = useSelector(state => state.chatBot.messages);
	const [chatInput, setChatInput] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const lastMessage = allMessages[allMessages.length - 1];
	const iaSelectedPrtUrl = buildPath(lastMessage.url);
	const productUrl = lastMessage.productUrl;
	const productContent = lastMessage.productContent;
	const wdUrl = lastMessage.wdUrl;
	const wdContent = lastMessage.wdContent;
	const similarProductsUrl = lastMessage.similarProductsUrl;
	const similarProductsContent = lastMessage.similarProductsContent;
	const fullURL = window.location.href;
	const path = window.location.pathname;
	const category = CATEGORY_HIERARCHY.categories.find(cat => cat.pathParam === lastMessage.component);
	const abbreviation = category ? t(`category:${category.id}.abbreviation`) : null;
	const messagesEndRef = useRef(null);
	const lang = getCurrentLanguage();
	const introTextAnnouncement = reactStringReplace(
		t('main:pages.home.categories.announcement.message', { domain: domain }),
		/(https?:\/\/\S+)/g,
		(match, i) => (
			<Link href={match} key={i} target="_blank" color="#2A78B8" tabIndex={0}>
				<Typography variant="subtitle1" color="#2A78B8" display="inline" style={{ fontWeight: 700 }}>
					{t('main:pages.home.categories.announcement.match' + i)}
				</Typography>
			</Link>
		)
	);

	const [selectedFilter, setSelectedFilter] = useState({
		type: null,
		value: null
	});
	const [selectedSearchCategory, setSelectedSearchCategory] = useState(null);
	const filteredCategories = useMemo(() => {
		if (selectedFilter.type && selectedFilter.value) {
			return CATEGORY_HIERARCHY.categories.filter(category => {
				const tech = category[selectedFilter.type];
				if (Array.isArray(tech)) {
					return tech.includes(selectedFilter.value);
				} else if (Array.isArray(selectedFilter.value)) {
					return selectedFilter.value.includes(tech);
				} else {
					return tech === selectedFilter.value;
				}
			});
		}
		return CATEGORY_HIERARCHY.categories;
	}, [selectedFilter]);

	const matchingCategories = useMemo(() => {
		if (selectedSearchCategory === 'CATEGORY_NOT_FOUND') {
			return filteredCategories;
		} else if (selectedSearchCategory !== null) {
			return [selectedSearchCategory];
		} else {
			return filteredCategories;
		}
	}, [selectedSearchCategory, filteredCategories]);

	const innerSpacing = 2;

	const handleOpenChat = initialMessage => {
		setShowChat(true);
		setChatInput(initialMessage);
	};

	useEffect(() => {
		setSelectedSearchCategory(null);
	}, [selectedFilter.value]);

	const createMessageType = (
		id,
		component = null,
		text,
		sender,
		url = null,
		wdUrl = null,
		wdContent = null,
		similarProductsUrl = null,
		similarProductsContent = null,
		productUrl = null,
		productContent = null,
		filters = []
	) => ({
		id,

		component,

		text,

		sender,

		url,

		wdUrl,

		wdContent,

		similarProductsUrl,

		similarProductsContent,

		productUrl,

		productContent,

		filters
	});

	//const isBot = lastMessage.sender === 'bot';

	const response = React.useState(null);

	// eslint-disable-next-line no-unused-vars
	const [loaded, setLoaded] = React.useState(null);

	const [filterRequest, setFilterRequest] = React.useState(false);

	// eslint-disable-next-line no-unused-vars

	const [input, setInput] = useState('');

	const [newFilters, setNewFilters] = useState([]);

	const [id, setID] = useState(1);

	const appliedState = useSelector(state => state.filters.appliedState);

	const history = useSelector(state => state.chatBot.history);

	const dispatch = useDispatch();

	const { updateFilterValue } = useUpdateFilterValue();

	const inputRef = useRef(input);

	const categoryId = matchingCategories ? matchingCategories.id : null;

	const pathParam = matchingCategories ? matchingCategories.pathParam : null;

	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [allMessages.length]);

	useEffect(() => {
		newFilters.forEach(f => {
			if (!f) return;

			const newValue = f.values.map(a => ((+a).toString() === a ? +a : a));

			updateFilterValue(f.cid, categoryId, newValue);
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [newFilters, categoryId]);

	useEffect(() => {
		if (filterRequest) {
			let newId = id + 1;

			setID(newId);

			dispatch(updateMessages(createMessageType(newId, response.component, response.content, 'bot')));

			setFilterRequest(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterRequest, allMessages, id, setFilterRequest]);

	useEffect(() => {
		if (chatInput) {
			setInput(chatInput => chatInput);

			handleSend();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatInput]);

	const handleSend = () => {
		if (!!chatInput) {
			inputRef.current = chatInput;

			setChatInput('');
		} else if (!!input) {
			inputRef.current = input;

			setInput('');
		} else inputRef.current = '';

		if (inputRef.current.trim() !== '') {
			setLoaded(false);

			let newId = id + 1;

			setID(newId);

			dispatch(updateMessages(createMessageType(newId, null, inputRef.current.trim(), 'U')));

			setIsLoading(true);

			const usedFilters = retrieveUsedFilters(appliedState);

			const inputApiParam = {
				request: inputRef.current.trim(),

				usedFilters,

				previousComponent: pathParam,

				history,

				language: language.toUpperCase()
			};

			api

				.getGPTResponse(inputApiParam)

				.then(res => {
					let newId = id + 1;
					setID(newId);

					if (!res.url) {
						const newFilters = res.filters;

						if (newFilters != null && newFilters.length > 0) {
							setNewFilters(newFilters);
						}
					}
					dispatch(
						updateMessages(
							createMessageType(
								newId,
								res.component,
								res.content,
								'bot',
								res.url,
								res.wdUrl,
								res.wdContent,
								res.similarProductsUrl,
								res.similarProductsContent,
								res.productUrl,
								res.productContent
							)
						)
					);

					dispatch(updateHistory(inputRef.current.trim()));

					setIsLoading(false);
				})

				.catch(error => {
					console.error('ERROR', error);

					setIsLoading(false);
				});
		}
	};

	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [allMessages.length]);

	return (
		<Grid container direction="column">
			<Grid item className={classes.mainHeader}>
				<Typography variant="h3">{t('components.header.title')}</Typography>
			</Grid>

			<Grid item container className={classes.mainContent}>
				<Grid item md={3} lg={3} xl={3} className={classes.filters}>
					<Filters selected={selectedFilter} setSelected={setSelectedFilter} />
					{/* will be used later */}
					{/* <CustomInfoCard>
						<Typography variant="h5" style={{ fontWeight: 700, color: '#212529' }}>
							{t('components.categoriesDashboard.infoCard.title')}
						</Typography>
						<Typography variant="subtitle1">{t('components.categoriesDashboard.infoCard.text')}</Typography>
					</CustomInfoCard> */}
				</Grid>

				<Grid
					item
					container
					direction="column"
					md={9}
					lg={9}
					xl={9}
					spacing={innerSpacing}
					className={classes.cardSection}>
					<Grid item style={{ paddingBottom: 24 }}>
						<Typography variant="h2" style={{ fontWeight: 700 }} gutterBottom>
							{t('main:pages.home.categories.title')}
						</Typography>

						<Typography variant="subtitle1" color="#212529">
							{reactStringReplace(
								t('main:pages.home.categories.subtitle'),
								'^recommendationAlgorithms^',
								(match, i) => (
									<AlgorithmPopover key={i} label={t('main:pages.home.categories.' + match)} />
								)
							)}
							{lang === 'cn'
								? introTextAnnouncement.map(item => (typeof item === 'string' ? item.trim() : item))
								: introTextAnnouncement}
						</Typography>
					</Grid>
					<Grid item container direction="column" spacing={innerSpacing}>
						<Grid item>
							<Search
								selectedFilter={selectedFilter}
								filteredCategories={filteredCategories}
								setSelectedCategory={setSelectedSearchCategory}
								handleOpenChat={handleOpenChat}
							/>
						</Grid>
						{showChat && (
							<Grid item>
								<div style={{ marginTop: '15px' }}>
									<Grid alignItems="flex-start" flexDirection="row">
										<Box>
											{isLoading && <LoadingDots />}
											{!isLoading && (
												<div>
													<Box className={classes.messageBox}>
														<Avatar alt="AIChat" src={CHAT_ICON} sx={{ width: 50, height: 50 }} />
														<Paper variant="outlined" className={classes.paper}>
															<Typography variant="body1" component="span">
																{lastMessage.text}{' '}
																{iaSelectedPrtUrl && abbreviation && (
																	<Link href={iaSelectedPrtUrl} color={'primary'}>
																		{t('main:components.chatbot.redirect')} {abbreviation}.{' '}
																	</Link>
																)}
															</Typography>
															{wdUrl && (
																<Typography variant="body1" component="span">
																	{' '}
																	{wdContent}{' '}
																	<Link href={wdUrl} color={'primary'} target="_blank">
																		{t('main:components.chatbot.wdTool')}.
																	</Link>
																</Typography>
															)}
															{productUrl && (
																<Typography variant="body1" component="span">
																	{' '}
																	{productContent}
																	<RenderChatbotLink
																		productPageLink={productUrl}
																		fullURL={fullURL}
																		path={path}
																		domain={domain}
																	/>
																</Typography>
															)}
															{similarProductsUrl && (
																<Typography variant="body1" component="span">
																	{' '}
																	{similarProductsContent}{' '}
																	<Link href={similarProductsUrl} color={'primary'} target="_blank">
																		{t('main:components.chatbot.simProducts')}
																	</Link>
																</Typography>
															)}
														</Paper>
													</Box>
													<div className={classes.disclaimer}>
														<Typography variant="body2">{t('main:components.chatbot.disclaimer')}</Typography>
													</div>
												</div>
											)}
											<div ref={messagesEndRef}></div>
										</Box>
									</Grid>
								</div>
							</Grid>
						)}
						{matchingCategories.length > 0 && (
							<Grid id="Home_Page__category_cards_container" item container spacing={innerSpacing}>
								{matchingCategories.map(cat => (
									<Grid
										key={cat.id + '-category-card'}
										item
										className={classes.cards}
										xs={12}
										sm={6}
										md={6}
										lg={6}
										xl={4}>
										<CategoryCard
											categoryId={cat.id}
											pathParam={cat.pathParam}
											abbreviation={t('category:' + cat.id + '.abbreviation')}
											title={t('category:' + cat.id + '.title')}
											description={t('category:' + cat.id + '.description')}
											image={CATEGORY_IMAGES[cat.id]}
											released={cat.released}
											wdSupported={cat.wdSupported}
											chartSupported={cat.chartSupported}
											spiceLiveModelSupported={cat.spiceLiveModelSupported}
											elitePairingStudioSupported={cat.elitePairingStudioSupported}
											redirectParam={cat.redirectParam}
										/>
									</Grid>
								))}
							</Grid>
						)}
					</Grid>
					<SupportCards />
				</Grid>
			</Grid>
		</Grid>
	);
};

function retrieveUsedFilters(appliedState) {
	if (!appliedState) return [];

	const istDirtyKeys = Object.keys(appliedState).filter(k => appliedState[k].isDirty);

	const usedFilters = [];

	istDirtyKeys.forEach(k => {
		const f = {};

		f.cid = k;

		f.values = [].concat(appliedState[k].value);

		usedFilters.push(f);
	});

	return usedFilters;
}

export default CategoriesDashboard;
